import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Etablissement {
  id: string;
  organization_id: string;
  nom: string;
  numero_finess: string | null;
  numero_siret: string | null;
  type_structure: string | null;
  adresse: string | null;
  code_postal: string | null;
  ville: string | null;
  region: string | null;
  telephone: string | null;
  nom_responsable: string | null;
  qualite_responsable: string | null;
  actif: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface EtablissementContextValue {
  etablissement: Etablissement | null;
  etablissements: Etablissement[];
  setEtablissementActif: (id: string) => void;
  isMultiSite: boolean;
  isLoading: boolean;
}

const EtablissementCtx = createContext<EtablissementContextValue>({
  etablissement: null,
  etablissements: [],
  setEtablissementActif: () => {},
  isMultiSite: false,
  isLoading: true,
});

export function useEtablissement() {
  return useContext(EtablissementCtx);
}

/**
 * Provider pour le contexte établissement actif
 * 
 * V1 : 1 seul établissement par organization, isMultiSite = false
 * V2 : sélecteur d'établissement si > 1
 */
export function EtablissementProvider({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const [actifId, setActifId] = React.useState<string | null>(null);

  const { data: etablissements = [], isLoading } = useQuery({
    queryKey: ['etablissements', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('etablissements')
        .select('*')
        .eq('organization_id', orgId)
        .eq('actif', true)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Etablissement[];
    },
    enabled: !!orgId,
  });

  // Établissement actif = sélectionné ou premier par défaut
  const etablissement = useMemo(() => {
    if (etablissements.length === 0) return null;
    if (actifId) {
      const found = etablissements.find(e => e.id === actifId);
      if (found) return found;
    }
    return etablissements[0];
  }, [etablissements, actifId]);

  const isMultiSite = etablissements.length > 1;

  const value: EtablissementContextValue = {
    etablissement,
    etablissements,
    setEtablissementActif: setActifId,
    isMultiSite,
    isLoading,
  };

  return (
    <EtablissementCtx.Provider value={value}>
      {children}
    </EtablissementCtx.Provider>
  );
}
